import React, { useState, useCallback } from 'react';
import { AppContext } from '../../contexts/AppContext';
import { ThemeContext, ThemeContextData } from '../../contexts/ThemeContext';
import { Link } from 'react-router-dom';
import { FiArrowRight } from 'react-icons/fi';
import SEOHead from '../../components/seo/SEOHead';
import { websiteSchema, createFAQSchema } from '../../components/seo/JsonLd';
import ScrollReveal from '../../components/ui/ScrollReveal';
import Container from '../../components/ui/Container';
import ScrollProgress from '../../components/ui/ScrollProgress';
import CursorGlow from '../../components/ui/CursorGlow';
import IntroLoader from './sections/IntroLoader';
import HeroSection from './sections/HeroSection';
import StatsSection from './sections/StatsSection';
import MarqueeSection from './sections/MarqueeSection';
import WhyCalisthenicsSection from './sections/WhyCalisthenicsSection';
import ProgramsShowcase from './sections/ProgramsShowcase';
import FlipCardsSection from './sections/FlipCardsSection';
import TestimonialsSection from './sections/TestimonialsSection';
import FAQSection from './sections/FAQSection';
import CTASection from './sections/CTASection';
import "./HomePage.css";

function HomePage() {
  const appContext = React.useContext(AppContext);
  const themeContext = React.useContext(ThemeContext);
  const theme = ThemeContextData[themeContext];
  const { carouselImages, otherSections } = appContext.homePageData;

  const [introComplete, setIntroComplete] = useState(
    () => typeof window !== 'undefined' && sessionStorage.getItem('gripItIntroSeen') === 'true'
  );

  const handleIntroComplete = useCallback(() => {
    setIntroComplete(true);
  }, []);

  const whyCalisthenicsSection = otherSections.find(s => s.title === "Why Calisthenics?" || (!s.type && !s.cards));
  const cardsSection = otherSections.find(s => s.type === "cards");
  const flipCardsSection = otherSections.find(s => s.type === "cards-flip");
  const testimonySection = otherSections.find(s => s.type === "testimony");
  const faqSection = appContext.homePageData.faqs;

  return (
    <CursorGlow>
      <div className={`${theme.textNormal} ${theme.background} relative overflow-hidden`}>
        <SEOHead
          title="Online Calisthenics Classes India — Bodyweight Training for Women & Men"
          description="Join India's top online calisthenics platform by Srushti Shah. Expert-coached bodyweight workouts for women, men, beginners & advanced. Pre/post natal programs, group classes, and 2000+ students trained. Start your free trial today."
          canonical="/"
          jsonLd={[websiteSchema, ...(faqSection ? [createFAQSchema(faqSection)] : [])]}
          keywords="calisthenics online, online calisthenics classes, calisthenics India, online workout India, bodyweight training, women workout, female workout, pre natal workout, post natal workout, working women workout, how to do calisthenics, calisthenics for beginners, online workout classes, grip it, srushti shah, home workouts, pull ups, push ups, strength training"
        />

        <ScrollProgress />

        {!introComplete && <IntroLoader onComplete={handleIntroComplete} />}

        <HeroSection carouselImages={carouselImages} introComplete={introComplete} />
        <StatsSection />
        <MarqueeSection />
        <WhyCalisthenicsSection data={whyCalisthenicsSection} />
        <ProgramsShowcase data={cardsSection} />
        <FlipCardsSection data={flipCardsSection} />
        <TestimonialsSection data={testimonySection} />
        <FAQSection data={faqSection} />
        <CTASection />

        {/* Dynamic remaining sections */}
        {otherSections
          .filter(s => s !== whyCalisthenicsSection && s !== cardsSection && s !== flipCardsSection && s !== testimonySection && s.type === 'mainSection')
          .map((section, index) => (
            <section key={`other-${index}`} className={`relative bg-cover bg-center py-section ${theme.backgroundSecondary}`} style={{ backgroundImage: `url(${section.image?.url})` }}>
              <div className="absolute inset-0 bg-brand-900/60" />
              <Container className="relative z-10 text-center py-10">
                <ScrollReveal>
                  <h2 className="text-4xl font-heading font-bold text-white mb-6">{section.title}</h2>
                  <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">{section.description}</p>
                  <Link
                    to={section.ctaButton.link}
                    className="inline-flex items-center gap-2 px-8 py-3 text-lg font-semibold text-white bg-brand-500 hover:bg-brand-600 rounded-xl transition-all shadow-lg"
                  >
                    {section.ctaButton.text}
                    <FiArrowRight className="w-5 h-5" />
                  </Link>
                </ScrollReveal>
              </Container>
            </section>
          ))}
      </div>
    </CursorGlow>
  );
}

export default HomePage;
